import { useEffect } from "react"
import { useChat } from "@/features/chat"
import { useLogger, ANALYTICS_EVENTS } from "@/features/analytics"
import { advisorsList } from "@/features/advisors"
import { getParamsFromStorage, setParamsToStorage } from "@/shared/lib/storage-params"
import GiftImage from "@/assets/img/advisors/advisor-gift.webp"
import GiftBg from "@/assets/img/advisors/bg/advisor-gift-bg.webp"

interface ChatWithAdvisorModalProps {
  onClose: () => void
  onNavigateToReadings: () => void
}

/**
 * Post-login "starter gift" popup. Selecting it picks an advisor and routes to
 * the readings/chat screen. Ported from lpr-web ChatWithAdvisorModal.
 */
export function ChatWithAdvisorModal({ onClose, onNavigateToReadings }: ChatWithAdvisorModalProps) {
  const { user, setSelectedAdvisor, threads } = useChat()
  const { trackAppyCntEvent } = useLogger()
  const { hand_photo_base64 } = getParamsFromStorage(["hand_photo_base64"])

  const eventData = (advisor?: { name: string; assistantId: string }) => ({
    user: { uid: user?.uid, name: user?.displayName, host: window.location.host },
    advisor: {
      name: advisor?.name,
      id: advisor?.assistantId,
      message: hand_photo_base64 ? "With palm photo message" : "Without palm photo message",
    },
  })

  useEffect(() => {
    trackAppyCntEvent(ANALYTICS_EVENTS.CHAT_WITH_ADVISOR_POPUP_SHOWN_AFTER_LOGIN, undefined, eventData())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRedeem = async () => {
    if (threads && threads.length > 0) {
      setParamsToStorage({ redirectToAdvisor: "false" })

      const advisor = hand_photo_base64
        ? advisorsList[12]
        : advisorsList.find((a) => a.assistantId === threads[threads.length - 1].assistantId)

      if (advisor) {
        setSelectedAdvisor(advisor)
        onNavigateToReadings()
        trackAppyCntEvent(
          ANALYTICS_EVENTS.CHAT_WITH_ADVISOR_POPUP_CLICK_AFTER_LOGIN,
          undefined,
          eventData(advisor),
        )
      }
    }
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 px-4 backdrop-blur-sm">
      <div className="relative w-full max-w-app overflow-hidden rounded-t-[20px] border-t border-purple-light/10 bg-dark-surface">
        {/* Cosmic glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(90%_50%_at_50%_-10%,var(--main-purple),transparent_70%)] opacity-30"
        />

        <div className="relative p-5 text-center">
          <p className="font-display text-2xl text-purple-light">You've Received a Personal Gift</p>
          <p className="mt-2 font-body text-base text-purple-light/70">
            We’ve unlocked a <span className="font-bold text-peach">free 3-minute session</span>{" "}
            just for you. Ask your question and get instant guidance from a real astrologer — no
            strings attached.
          </p>

          <div
            className="mt-5 rounded-[20px] border border-purple-light/10 bg-card-purple bg-contain bg-center bg-no-repeat p-4"
            style={{ backgroundImage: `url(${GiftBg})` }}
          >
            <img src={GiftImage} alt="Gift" className="mx-auto max-w-[252px] object-contain" />
            <div className="mx-auto mt-[5px] flex h-8 w-fit items-center justify-center rounded-2xl bg-green px-[15px] text-[10px] font-bold uppercase text-dark-base">
              Get Your Starter Gift
            </div>
            <p className="mt-2.5 font-display text-2xl uppercase text-gold">FREE 3 MINUTES</p>
          </div>

          <button
            type="button"
            onClick={handleRedeem}
            className="mt-5 h-14 w-full rounded-[28px] bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white transition-opacity hover:opacity-90"
          >
            Redeem now
          </button>
        </div>
      </div>
    </div>
  )
}
